/**
 * Document review — the agent/broker's queue of documents waiting on a HUMAN.
 *
 * Two things land here, and the difference matters to whoever is deciding:
 *   AWAITING_APPROVAL — nothing confirmed it. Either the type was never
 *     verified (V074) or the NAME on an identity document matched neither the
 *     client nor their spouse, which is how a spouse's licence used to file
 *     itself as the client's proof of identity.
 *   REJECTED — a check actively failed (stale, missing fields, a cross-document
 *     conflict). An agent can still override it, but the API demands a written
 *     reason, so the prompt below asks for one.
 *
 * Decisions go to the real endpoint and the list is refetched from the server
 * rather than patched locally: approving can COMPLETE the dossier and fire the
 * client's form packet, so what the screen shows afterwards has to be what the
 * backend actually did.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@re-platform/ui";

import { ApiClientError, openDocument } from "@re-platform/api-client";

import { api } from "../lib/api";
import "./DocumentReview.css";

/** Mirrors ReviewQueueItemOut (services/api/document/src/schemas/dossiers.py). */
interface ReviewItem {
  dossier_id: string;
  template_item_id: string;
  document_id: string | null;
  client_id: string;
  client_name: string | null;
  application_number: number | null;
  doc_type_key: string | null;
  display_name: string | null;
  status: "AWAITING_APPROVAL" | "REJECTED";
  reason: string | null;
  uploaded_at: string | null;
}

/** The slot is the unit of decision, not the document — a re-upload replaces
 * the file but the requirement stays the same row. */
const keyOf = (d: ReviewItem) => `${d.dossier_id}:${d.template_item_id}`;

function whenUploaded(iso: string | null): string {
  if (!iso) return "not uploaded";
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return new Date(iso).toLocaleDateString();
}

// ---- inline icons -----------------------------------------------------------

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}
const FileIcon = () => (
  <Svg>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </Svg>
);
const WarnIcon = () => (
  <Svg>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </Svg>
);
// ---- card -------------------------------------------------------------------

function DocCard({
  d,
  busy,
  viewing,
  onApprove,
  onReject,
  onView,
}: {
  d: ReviewItem;
  busy: boolean;
  viewing: boolean;
  onApprove: () => void;
  onReject: () => void;
  onView: () => void;
}) {
  const rejected = d.status === "REJECTED";

  return (
    <div className={`dr-card ${rejected ? "dr-card--warn" : ""}`}>
      <div className={`dr-card__icon ${rejected ? "dr-card__icon--warn" : ""}`}>
        {rejected ? <WarnIcon /> : <FileIcon />}
      </div>

      <div className="dr-card__body">
        <div className="dr-card__title">
          {d.display_name ?? d.doc_type_key ?? "Document"}
        </div>
        <div className="dr-card__meta">
          {[
            d.client_name ?? "Unknown client",
            d.application_number
              ? `Application #${d.application_number}`
              : null,
            `uploaded ${whenUploaded(d.uploaded_at)}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
        <div className="dr-card__pills">
          <span
            className={`dr-pill ${rejected ? "dr-pill--danger" : "dr-pill--warn"}`}
          >
            {rejected ? "Rejected by a check" : "Needs review"}
          </span>
        </div>
        {/* The reason IS the review. Without it an agent is being asked to
            approve a document without being told what was wrong with it. */}
        {d.reason && <div className="dr-card__meta">{d.reason}</div>}
      </div>

      <div className="dr-card__actions">
        {/* The document itself. Approving without it is asking an agent to
            certify that a file they have never seen is the right one — and
            "Rejected by a check" is precisely the case where the machine's
            opinion most needs a human to look before it is overruled. */}
        {d.document_id && (
          <button
            className="dr-btn"
            onClick={onView}
            disabled={viewing}
            title="Open the uploaded file"
          >
            {viewing ? "Opening…" : "View"}
          </button>
        )}
        <button
          className="dr-btn dr-btn--approve"
          onClick={onApprove}
          disabled={busy}
        >
          {busy ? "Saving…" : "Approve"}
        </button>
        <button
          className="dr-btn dr-btn--danger"
          onClick={onReject}
          disabled={busy}
        >
          Reject
        </button>
      </div>
    </div>
  );
}

// ---- screen -----------------------------------------------------------------

export function DocumentReview() {
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["review-queue"],
    queryFn: () => api.request<ReviewItem[]>("/document/review-queue"),
  });

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  const decide = useMutation({
    mutationFn: async (vars: {
      d: ReviewItem;
      decision: "approve" | "reject";
      reason?: string;
    }) =>
      api.request<{ status: string; dossier_completed: boolean }>(
        `/document/dossiers/${vars.d.dossier_id}/slots/${vars.d.template_item_id}/approval`,
        {
          method: "POST",
          body: {
            decision: vars.decision,
            reason: vars.reason,
            // Spec 019: the document on screen. If the client replaced it, the
            // server answers 409 instead of deciding a document nobody opened.
            document_id: vars.d.document_id ?? undefined,
          },
        },
      ),
    onMutate: (vars) => setPending(keyOf(vars.d)),
    onSettled: () => setPending(null),
    onSuccess: (res, vars) => {
      // Refetch rather than patch: approving the LAST outstanding requirement
      // completes the dossier and sends the client's form packet, and a locally
      // edited list would quietly disagree with what actually happened.
      void qc.invalidateQueries({ queryKey: ["review-queue"] });
      void qc.invalidateQueries({ queryKey: ["dossiers"] });
      showToast(
        vars.decision === "approve"
          ? res.dossier_completed
            ? "Approved — that was the last document, so the client's agreements are on their way."
            : "Approved into the dossier."
          : "Rejected — the client has been told what to send instead.",
      );
    },
    onError: (err) => {
      showToast(err instanceof Error ? err.message : "That didn't save.");
      // 409: the row changed under the agent (spec 019) — show what is there now.
      if (err instanceof ApiClientError && err.status === 409)
        void qc.invalidateQueries({ queryKey: ["review-queue"] });
    },
  });

  /** Open the uploaded file in a new tab.
   *
   * Same presigned-GET path the client's own dossier uses — the URL is signed
   * and short-lived, so it is opened rather than fetched (fetching it would
   * pull the bytes through the browser for nothing, and CORS would refuse). */
  async function view(d: ReviewItem) {
    if (!d.document_id) return;
    setViewingId(d.document_id);
    try {
      await openDocument(api, d.document_id);
    } catch (err) {
      showToast(
        err instanceof Error
          ? err.message
          : "Couldn't open that file just now.",
      );
    } finally {
      setViewingId(null);
    }
  }

  function approve(d: ReviewItem) {
    // Overriding a REJECTED document requires a written reason (the API 422s
    // without one): a person overruling a documented check owes the record why.
    let reason: string | undefined;
    if (d.status === "REJECTED") {
      const typed = window.prompt(
        "This document was rejected by a check. Why is it acceptable?",
      );
      if (!typed?.trim()) return;
      reason = typed.trim();
    }
    decide.mutate({ d, decision: "approve", reason });
  }

  function reject(d: ReviewItem) {
    const typed = window.prompt("What should the client send instead?");
    if (!typed?.trim()) return;
    decide.mutate({ d, decision: "reject", reason: typed.trim() });
  }

  const docs = q.data ?? [];
  const needs = docs.filter((d) => d.status === "AWAITING_APPROVAL");
  const rejected = docs.filter((d) => d.status === "REJECTED");
  const [tab, setTab] = useState<"needs" | "rejected">("needs");
  const list = tab === "needs" ? needs : rejected;

  return (
    <div className="dr">
      {toast && <div className="dr-toast">{toast}</div>}

      <h1 className="dr__title">Document review</h1>
      <p className="dr__subtitle">
        Documents nothing could confirm on its own — an unverified type, or a
        name that matched neither the client nor their spouse. Approving
        satisfies the requirement exactly as an automatic check would, and
        records that you were the one who said so. Cleanly matched documents
        never appear here.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Button
          size="sm"
          variant={tab === "needs" ? "primary" : "secondary"}
          onClick={() => setTab("needs")}
        >
          Needs review · {needs.length}
        </Button>
        <Button
          size="sm"
          variant={tab === "rejected" ? "primary" : "secondary"}
          onClick={() => setTab("rejected")}
        >
          Rejected · {rejected.length}
        </Button>
      </div>

      <div className="dr-list">
        {q.isPending && <p className="dr-empty">Loading…</p>}
        {q.isError && (
          <p className="dr-empty">
            Couldn&apos;t load the queue.{" "}
            {q.error instanceof Error ? q.error.message : ""}
          </p>
        )}
        {!q.isPending && !q.isError && list.length === 0 && (
          <p className="dr-empty">
            {tab === "needs"
              ? "Nothing to review — you're all caught up."
              : "Nothing rejected."}
          </p>
        )}
        {list.map((d) => (
          <DocCard
            key={keyOf(d)}
            d={d}
            busy={pending === keyOf(d)}
            viewing={viewingId === d.document_id}
            onApprove={() => approve(d)}
            onReject={() => reject(d)}
            onView={() => void view(d)}
          />
        ))}
      </div>
    </div>
  );
}
